import {
  createRoute,
  Link,
  Outlet,
  useNavigate,
  useBlocker,
  useRouter,
  useRouterState,
} from '@tanstack/react-router'
import { Button, EntryFrame } from '@volee/ui'
import { useEffect, useRef, useState } from 'react'

import { messages } from '../messages/index.ts'
import { NAV_PATHS } from '../navigation/paths.ts'
import { rootRoute } from './root.tsx'

// The frame every entrance screen stands in: the photo stays and the sheet changes. A route is
// an entrance route by being a child of this one (apps/frontend/docs/ingresso-a-tutto-schermo.md).
export const entryRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'entry',
  staticData: { chrome: 'entry' },
  component: EntryLayout,
})

/** Whether the last move opened the sheet from the door: then the door is one step back, and
 *  closing is going there, which leaves the history as it was. */
function useOpenedFromTheDoor() {
  const router = useRouter()
  const opened = useRef(false)
  useEffect(
    () =>
      router.subscribe('onResolved', ({ fromLocation, toLocation }) => {
        opened.current =
          fromLocation?.pathname === NAV_PATHS.home && toLocation.pathname !== NAV_PATHS.home
      }),
    [router],
  )
  return opened
}

/** Keeps the sheet closed from the moment it was closed until the router has committed the matches
 *  of the door: the location changes first, and a sheet that reads the old matches in between
 *  comes back (apps/frontend/docs/ingresso-a-tutto-schermo.md § «La chiusura»). */
function useSheetExit(view: string | undefined) {
  const router = useRouter()
  const [closingView, setClosingView] = useState<string | null>(null)
  const finish = useRef<((blocked: boolean) => void) | null>(null)
  if (closingView !== null && view !== closingView) setClosingView(null)

  useBlocker({
    enableBeforeUnload: false,
    shouldBlockFn: ({ current, next }) => {
      if (next.pathname !== NAV_PATHS.home || current.pathname === NAV_PATHS.home) return false
      if (finish.current !== null) return true
      setClosingView(router.state.matches.at(-1)?.routeId ?? null)
      return new Promise<boolean>((resolve) => {
        finish.current = resolve
      })
    },
  })
  useEffect(() => () => finish.current?.(true), [])

  function onClosed() {
    finish.current?.(false)
    finish.current = null
  }
  return { closing: closingView !== null && closingView === view, onClosed }
}

function EntryLayout() {
  const router = useRouter()
  const navigate = useNavigate()
  const openedFromTheDoor = useOpenedFromTheDoor()
  // The door is the one child with no sheet; any other child opens it, so a route added under
  // this one needs to declare nothing.
  const sheetOpen = useRouterState({
    select: (status) => status.matches.at(-1)?.staticData.entryDoor !== true,
  })
  const view = useRouterState({ select: (status) => status.matches.at(-1)?.routeId })
  const exit = useSheetExit(view)

  return (
    <EntryFrame
      label={messages.app.name}
      pauseLabel={messages.entry.pause}
      resumeLabel={messages.entry.resume}
      closeLabel={messages.entry.close}
      headline={messages.entry.headline}
      actions={
        <>
          <Button asChild size="xl" block>
            <Link to={NAV_PATHS.signIn}>{messages.signIn.submit}</Link>
          </Button>
          <Button asChild variant="quiet" size="lg" block>
            <Link to={NAV_PATHS.signUp}>{messages.signIn.registerClub}</Link>
          </Button>
        </>
      }
      open={sheetOpen && !exit.closing}
      onClosed={exit.onClosed}
      onClose={() => {
        if (openedFromTheDoor.current) router.history.back()
        else void navigate({ to: NAV_PATHS.home, replace: true })
      }}
      viewKey={view}
    >
      <Outlet />
    </EntryFrame>
  )
}
